import Base64 from "crypto-js/enc-base64"
import MD5 from "crypto-js/md5"
import load from "load-script"

import Script from "./script"

class Ad {
  constructor(unitPath, size, sizeMapping = [], videoAd = false) {
    this.unitPath = unitPath
    this.size = size
    this.sizeMapping = sizeMapping
  }

  getSizes() {
    const sizeMapping = this.sizeMapping.filter(([[width, height]]) => {
      return window.matchMedia(
        `(min-width: ${width}px) and (min-height: ${height}px)`
      ).matches
    })

    sizeMapping.sort(([[width1, height1]], [[width2, height2]]) => {
      return width1 !== width2 ? width2 - width1 : height2 - height1
    })

    return [...sizeMapping.map(([mediaQuery, sizes]) => sizes), this.size][0]
  }
}

class AdManager extends Script {
  constructor(settings) {
    super({
      debug: false,
      exclusionContainerSelector: null,
      displayFunction: null,
      adUnits: {},
      adMapping: {},
      lazyload: {
        fetchMargin: 30,
        mobileScaling: 2.0,
      },
      setCentering: true,
      isContentSafe: true,
      onSlotLoad: (slot) => null,
      ...settings,
    })
    this.ads = this.initAds()
    this.adsReady = new Event("adsReady")
    this.firstAdSlotRendered = false
    this.observers = {}
    this.positions = {}
    this.slots = {}
    this.service = {
      cmd: [],
    }
  }

  load() {
    window.googletag = this.service
    const containers = this.getNewContainers()

    this.configure(containers)

    load("https://securepubads.g.doubleclick.net/tag/js/gpt.js")
    this._debugger("ad-manager", "done load()")

    this.initEvents()
  }

  getNewContainers() {
    const adPositionPairs = []
    Object.entries(this.ads).forEach(([adName, ad]) => {
      const selector = `[data-ad-manager-id="${adName}"]:not([id])`
      const availableSlots = Array.from(document.querySelectorAll(selector))

      availableSlots.forEach((position) => {
        this.positions[adName] = this.positions[adName] || []

        position.id = `${adName}-${this.positions[adName].length}`

        this.positions[adName].push(position)

        if (position.dataset.lazy) {
          const settings = {
            ...this.settings.lazyload,
            ...(this.settings.adUnits[adName] || { lazyload: {} }).lazyload,
          }
          const margin =
            settings.fetchMargin *
            (this.isMobile ? settings.mobileScaling : 1.0)

          this.observers[adName] = new IntersectionObserver(
            (items) => {
              this.fetch(
                items
                  .filter(
                    (item) =>
                      item.isIntersecting && !item.target.dataset.fetched
                  )
                  .map((item) => item.target)
              )
            },
            {
              rootMargin: `0% 0% ${margin}% 0%`,
            }
          )
          this.observers[adName].observe(position)
        } else if (position.dataset.delayed) {
          this.observers[adName] = new MutationObserver(
            (mutationsList, observer) => {
              for (const mutation of mutationsList) {
                if (
                  mutation.type === "attributes" &&
                  mutation.attributeName == "data-delayed"
                ) {
                  this.fetch([position])
                  this._debugger(
                    "ad-manager",
                    `Done delayed fetch() for ${adName}`
                  )
                  observer.disconnect()
                }
              }
            }
          )
          this.observers[adName].observe(position, {
            attributes: true,
            childList: false,
          })
          this._debugger("ad-manager", `Delayed fetch() for ${adName}`)
        } else {
          adPositionPairs.push({ ad, position })
        }
      })
    })
    return adPositionPairs
  }

  handleStickyAd(adContainer) {
    let parentContainer = adContainer.parentNode
    let parentWrapper = parentContainer.parentNode

    parentWrapper.classList.remove("d-none")
    let parentContainerHeight = parentContainer.offsetHeight

    parentContainer.previousElementSibling.addEventListener("click", (e) => {
      parentWrapper.classList.toggle("off")
      if (parentWrapper.classList.contains("off")) {
        parentWrapper.style.bottom = `-${parentContainerHeight}px`
      } else {
        parentWrapper.style.bottom = "0px"
      }
    })
  }

  initAds() {
    const ads = {}
    this.settings.displayAdsMapping.forEach((ad) => {
      if (!ad.onlyDebug || (ad.onlyDebug && this.debug))
        ads[ad.name] = new Ad(ad.path, ad.sizes)
      this._debugger(
        "ad-manager",
        `Added "${ad.name}" to AD Units${
          ad.onlyDebug ? " (showing only in debug)" : ""
        }`
      )
    })

    return ads
  }

  initEvents() {
    window.addEventListener("NetClosingInterstitial", () =>
      this.loadDelayedSlots()
    )
    this.push(() => {
      this.service.pubads().addEventListener("slotRenderEnded", (event) => {
        let adContainer = document.getElementById(event.slot.getSlotElementId())
        if (adContainer && adContainer.hasAttribute("data-interstitial")) {
          if (event.isEmpty) {
            this._debugger("ad-manager", `Interstitial ad is empty`)
            this.loadDelayedSlots()
          }
        }
        if (adContainer && adContainer.hasAttribute("data-sticky")) {
          this.handleStickyAd(adContainer)
          this._debugger("ad-manager", "Sticky ad loaded")
        }
        if (!this.firstAdSlotRendered) {
          this.firstAdSlotRendered = true
          this._debugger("ad-manager", "Dispatching adsReady event")
          window.dispatchEvent(this.adsReady)
        }
      })
    })
  }

  fetch(positions) {
    if (positions.length === 0) {
      return
    }

    positions.forEach((position) => {
      position.dataset.fetched = true
    })

    const adPositionPairs = positions.map((position) => ({
      ad: this.ads[position.dataset.adManagerId],
      position,
    }))

    this.defineSlots(adPositionPairs)
    this.display(adPositionPairs)
  }

  configure(containers) {
    this.push(() => {
      this.service.pubads().disableInitialLoad()
      this.service.pubads().enableSingleRequest()
      this.service.enableServices()
      this.service.pubads().setCentering(this.settings.setCentering)
      this.service.pubads().setTargeting("SITO", [window.location.hostname])
      this.service
        .pubads()
        .setTargeting("PAGINA", [
          this.getPage(),
          ...(this.settings.debug ? ["TEST"] : []),
        ])
      this.service
        .pubads()
        .setTargeting("URL", [Base64.stringify(MD5(this.getPath()))])
      this.service
        .pubads()
        .setTargeting(
          "PAGINAZIONE",
          window.galaxyNetworkInserter.isPaginated === "1" ? "YES" : "NO"
        )
      if (document.body.hasAttribute("data-unsafe")) {
        if (document.body.dataset.unsafe === "1") {
          this.service.pubads().setTargeting("SAFE", "NO")
        } else {
          this.service.pubads().setTargeting("SAFE", "YES")
        }
      }
      // Retrive UTMs from URL if present
      const utms = this.getUTMs()
      if (utms) {
        Object.keys(utms).forEach((key) => {
          this.service.pubads().setTargeting(key.toLocaleUpperCase(), utms[key])
        })
      }
    })

    if (containers.length > 0) {
      this.defineSlots(containers)
      this.display(containers)
    }

    this._debugger("ad-manager", "done configure()")
  }

  display(adPositionPairs) {
    if (adPositionPairs.length === 0) {
      return
    }

    if (this.settings.displayFunction != null) {
      this.push(() => {
        this.settings.displayFunction(adPositionPairs, this.slots, this)
      })
    } else {
      adPositionPairs.forEach(({ ad, position }) => {
        this.push(() => {
          this.service.display(position.id)
        })
      })
    }
  }

  push(command) {
    this.service.cmd.push(command)
  }

  defineSlots(adPositionPairs) {
    this.push(() => {
      adPositionPairs.forEach(({ ad, position }) => {
        const sizeMapping = this.service.sizeMapping()
        ad.sizeMapping.forEach(([viewportSize, slotSize]) => {
          sizeMapping.addSize(viewportSize, slotSize)
        })

        const slot = this.service.defineSlot(ad.unitPath, ad.size, position.id)
        slot.defineSizeMapping(sizeMapping.build())
        slot.addService(this.service.pubads())
        this.slots[position.id] = slot
      })
    })
  }

  getPath() {
    return window.location.pathname
  }

  getPage() {
    const fallback = "GENERICO"
    const homepage = "HOMEPAGE"
    const path = this.getPath()

    if (path === "/") {
      return homepage
    }
    return fallback
  }

  getUserAgent() {
    return window.navigator.userAgent || window.navigator.vendor || window.opera
  }

  getUTMs() {
    const utms = {}
    const utmParams = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
    ]
    const urlParams = new URLSearchParams(window.location.search)

    utmParams.forEach((param) => {
      if (urlParams.has(param)) {
        utms[param] = urlParams.get(param)
      }
    })

    return utms
  }

  isFacebookInAppBrowser() {
    const userAgent = this.getUserAgent()

    return userAgent.includes("FBAN") || userAgent.includes("FBAV")
  }
}

export default AdManager
